import _ from 'lodash';
import { getAnatomicRegionFromString } from 'app/utils/xrayRegions';

const MAX_ROTATION_HISTORY_DEPTH = 6;

/**
 * Build a species-agnostic key from an anatomic region string.
 * Strips the specie prefix, keeping mainRegion, subRegion and view.
 * e.g. "dog thorax" → "thorax", "cat membre_pelvien_gauche Grasset profil" → "membre_pelvien_gauche Grasset profil"
 */
export const getSpeciesAgnosticRegionKey = (anatomicRegion: string): string => {
  const { mainRegion, subRegion, view } = getAnatomicRegionFromString(anatomicRegion);
  return _.filter([mainRegion, subRegion, view]).join(' ');
};

/**
 * Normalize a viewport rotation to a canonical value independent of flip state.
 *
 * A vflip is equivalent to hflip + 180° rotation, so we normalize vflip first:
 *   - If vflip: toggle effectiveHflip, add 180° to rotation
 *   - If effectiveHflip: negate rotation (hflip inverts rotation sign)
 *
 * Result is normalized to [0, 360).
 */
export const normalizeRotation = (
  rotation: number = 0,
  hflip: boolean = false,
  vflip: boolean = false
): number => {
  let effectiveHflip = hflip;
  let normalizedRotation = rotation;

  // vflip is equivalent to hflip + 180° rotation
  if (vflip) {
    effectiveHflip = !effectiveHflip;
    normalizedRotation += 180;
  }

  // hflip negates the rotation
  if (effectiveHflip) {
    normalizedRotation = -normalizedRotation;
  }

  return ((normalizedRotation % 360) + 360) % 360;
};

/**
 * Get the most frequently used rotation from a history array.
 * On tie, returns the most recently recorded value among the tied candidates.
 * Returns undefined if history is empty.
 */
export const getMostFrequentRotation = (history: number[]): number | undefined => {
  if (!history || history.length === 0) return undefined;

  const counts = _.countBy(history);
  const maxCount = _.max(Object.values(counts));

  // Among all values with maxCount, find the most recently recorded one
  for (let i = history.length - 1; i >= 0; i--) {
    if (counts[history[i]] === maxCount) {
      return history[i];
    }
  }

  return undefined;
};

export { MAX_ROTATION_HISTORY_DEPTH };
